import { FC, useMemo, useState } from 'react';
import classNames from 'classnames';

import { Layer, useBreakpoint } from '@hh.ru/magritte-ui';

import useFeatureEnabled from 'src/hooks/useFeatureEnabled';
import useProfile from 'src/hooks/useProfile';
import GeoLocation from 'src/widgets/GeoLocation';

import HeaderLoginButton from 'src/components/Header/HeaderLoginButton';
import HeaderLogo from 'src/components/Header/HeaderLogo';
import HeaderMenuIcon from 'src/components/Header/HeaderMenuIcon';
import HeaderMobileNav from 'src/components/Header/HeaderMobileNav';
import HeaderNav from 'src/components/Header/HeaderNav';
import HeaderNavItem from 'src/components/Header/HeaderNavItem';

import styles from './styles.less';

interface HeaderProps {
    absolute?: boolean;
}

const Features = {
    isEnabledGeoButton: 'enabled_geo_location_button',
};

const Header: FC<HeaderProps> = ({ absolute }) => {
    const [isOpenMenu, setIsOpenMenu] = useState(false);
    const { isMobile } = useBreakpoint();
    const { isAuth } = useProfile();
    const isEnabledGeoButton = useFeatureEnabled(Features.isEnabledGeoButton);

    const headerClasses = classNames(styles.header, {
        [styles.headerStickRight]: isMobile && isAuth,
        [styles.headerAbsolute]: absolute,
    });

    const GeoComponent = useMemo(
        () =>
            isEnabledGeoButton ? (
                <HeaderNavItem Element="div">
                    <GeoLocation type="button" />
                </HeaderNavItem>
            ) : null,
        [isEnabledGeoButton]
    );

    const handleToggle = () => setIsOpenMenu((prevState) => !prevState);

    const renderHeader = () => {
        if (isMobile && isAuth) {
            return (
                <header className={headerClasses}>
                    <HeaderLoginButton />
                    <div className={styles.logoCenter}>
                        <HeaderLogo />
                    </div>
                    <HeaderMenuIcon onClick={handleToggle} />
                    <HeaderMobileNav isOpen={isOpenMenu} headerComponent={GeoComponent} onClose={handleToggle} />
                </header>
            );
        }

        if (isMobile && !isAuth) {
            return (
                <header className={headerClasses}>
                    <HeaderLogo />
                    <div className={styles.headerControls}>
                        <HeaderMenuIcon onClick={handleToggle} />
                        <HeaderLoginButton />
                    </div>
                    <HeaderMobileNav isOpen={isOpenMenu} headerComponent={GeoComponent} onClose={handleToggle} />
                </header>
            );
        }

        return (
            <header className={headerClasses}>
                <HeaderLogo />
                <HeaderNav />
                <div className={styles.headerControls}>
                    {GeoComponent}
                    <HeaderLoginButton />
                </div>
            </header>
        );
    };

    return absolute ? <Layer layer={'alpha'}>{renderHeader()}</Layer> : renderHeader();
};

export default Header;
